import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  HostListener,
  inject,
  Input,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { fromEvent, take } from 'rxjs';
import { DocumentScrollOnMouseMoveDirective } from './document-page-scroll.directive';

@Component({
  selector: 'et-atoms-document-page-preview',
  imports: [DocumentScrollOnMouseMoveDirective],
  templateUrl: './document-page-preview.component.html',
  styleUrl: './document-page-preview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentPagePreviewComponent {
  private dialog = inject(MatDialog);

  @Input({ required: true }) pageImage!: string;

  @ViewChild('previewModal', { read: TemplateRef })
  previewModal!: TemplateRef<HTMLDivElement>;

  @HostBinding('tabindex')
  tabindex = 0;

  /**
   * Handles the click event on the component.
   *
   * This method prevents the default behavior and stops the propagation of the click event
   * to avoid triggering parent event handlers.
   *
   * @param {MouseEvent} event - The mouse click event.
   */
  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
  }

  /**
   * Opens the preview modal dialog when the component is clicked.
   *
   * This method displays a modal dialog containing the preview of the page.
   * It listens for the mouseup event to close the dialog when the mouse button is released.
   * The method also prevents the default behavior and stops event propagation.
   *
   * @param {MouseEvent} event - The mouse down event that triggers the preview.
   */
  @HostListener('mousedown', ['$event'])
  onShowPreview(event: MouseEvent) {
    const dialogRef = this.dialog.open(this.previewModal, {
      panelClass: ['et-dialog-radius', 'et-dialog-padding'],
      width: '100%',
      maxWidth: '1200px',
      height: '99vh',
      autoFocus: false,
    });
    this.listenForMouseUp(dialogRef);
    event.stopPropagation();
    event.preventDefault();
  }

  /**
   * Listens for the mouseup event to close the preview modal dialog.
   *
   * This method subscribes to the mouseup event on the document and closes the dialog
   * when the event is triggered. The subscription is automatically unsubscribed after
   * the first event is received.
   *
   * @param {MatDialogRef<HTMLDivElement>} dialogRef - The reference to the open modal dialog.
   */
  private listenForMouseUp(dialogRef: MatDialogRef<HTMLDivElement>) {
    fromEvent(document, 'mouseup')
      .pipe(take(1))
      .subscribe(() => dialogRef.close());
  }
}
